/**
 * Route customer address slots (profile addr1–addr4).
 * Routes store `customer_address_slot`; API resolves to the current profile address.
 * Legacy routes keep a full address string in `customer_address` with no slot.
 */

export const ADDRESS_SLOTS = ["addr1", "addr2", "addr3", "addr4"];

export const formatStructuredAddress = (line1, line2, city, state, zipCode, note) => {
  const cityState = [city, state].filter(Boolean).join(", ");
  const mainAddress = [line1, line2, cityState, zipCode]
    .filter((item) => item && String(item).trim() !== "")
    .join(" ")
    .trim();
  const addressNote = `${note || ""}`.trim();
  if (!mainAddress) return "";
  return addressNote ? `${mainAddress} (${addressNote})` : mainAddress;
};

const formatLegacyCommaAddress = (line1, line2, city, state, zipCode, note) => {
  const mainAddress = `${line1 || ""}, ${line2 || ""}, ${city || ""}, ${state || ""} ${zipCode || ""}`
    .replace(/, ,/g, ",")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^,|,$/g, "")
    .trim();
  const addressNote = `${note || ""}`.trim();
  if (!mainAddress || mainAddress === ",") return "";
  return addressNote ? `${mainAddress} (${addressNote})` : mainAddress;
};

export const normalizeAddressText = (value) =>
  `${value || ""}`
    .toString()
    .toLowerCase()
    .replace(/\([^)]*\)/g, " ")
    .replace(/[^a-z0-9]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const getSlotParts = (customer, slot) => {
  if (!customer || !slot) return null;
  switch (slot) {
    case "addr1":
      return {
        line1: customer.address_line_1,
        line2: customer.address_line_2,
        city: customer.city,
        state: customer.state,
        zip: customer.zip_code,
        note: customer.address_note,
        legacy: customer.address1 || customer.address,
      };
    case "addr2":
      return {
        line1: customer.address2_line_1 || customer.street_address_2,
        line2: customer.address2_line_2,
        city: customer.city2,
        state: customer.state2,
        zip: customer.zip_code2,
        note: customer.address2_note,
        legacy: customer.address2,
      };
    case "addr3":
      return {
        line1: customer.address3_line_1 || customer.street_address_3,
        line2: customer.address3_line_2,
        city: customer.city3,
        state: customer.state3,
        zip: customer.zip_code3,
        note: customer.address3_note,
        legacy: customer.address3,
      };
    case "addr4":
      return {
        line1: customer.address4_line_1 || customer.street_address_4,
        line2: customer.address4_line_2,
        city: customer.city4,
        state: customer.state4,
        zip: customer.zip_code4,
        note: customer.address4_note,
        legacy: customer.address4,
      };
    default:
      return null;
  }
};

export const formatAddressFromSlot = (customer, slot) => {
  const parts = getSlotParts(customer, slot);
  if (!parts) return "";
  return (
    formatStructuredAddress(parts.line1, parts.line2, parts.city, parts.state, parts.zip, parts.note) ||
    `${parts.legacy || ""}`.trim() ||
    ""
  );
};

export const getCustomerAddressSlotOptions = (customer) => {
  if (!customer) return [];
  return ADDRESS_SLOTS.map((slot) => ({
    slot,
    address: formatAddressFromSlot(customer, slot),
  })).filter((item) => `${item.address || ""}`.trim() !== "");
};

const getSlotAddressVariants = (customer, slot) => {
  const parts = getSlotParts(customer, slot);
  if (!parts) return [];
  return [
    formatStructuredAddress(parts.line1, parts.line2, parts.city, parts.state, parts.zip, parts.note),
    formatStructuredAddress(parts.line1, parts.line2, parts.city, parts.state, parts.zip, ""),
    formatLegacyCommaAddress(parts.line1, parts.line2, parts.city, parts.state, parts.zip, parts.note),
    formatLegacyCommaAddress(parts.line1, parts.line2, parts.city, parts.state, parts.zip, ""),
    `${parts.legacy || ""}`.trim(),
  ].filter((v) => `${v || ""}`.trim() !== "");
};

export const matchAddressTextToSlot = (customer, addressText) => {
  const normalized = normalizeAddressText(addressText);
  if (!normalized || !customer) return null;
  for (const slot of ADDRESS_SLOTS) {
    const variants = getSlotAddressVariants(customer, slot);
    for (const variant of variants) {
      if (normalizeAddressText(variant) === normalized) return slot;
    }
  }
  return null;
};

export const isValidAddressSlot = (slot) => ADDRESS_SLOTS.includes(`${slot || ""}`.trim());

export const hasAddressSlot = (routeCustomer) => isValidAddressSlot(routeCustomer?.customer_address_slot);

export const resolveRouteCustomerAddress = (routeCustomer, customerProfile) => {
  const override = `${routeCustomer?.customer_address_override || ""}`.trim();
  if (override) return override;

  const slot = `${routeCustomer?.customer_address_slot || ""}`.trim();
  if (isValidAddressSlot(slot) && customerProfile) {
    const fromSlot = formatAddressFromSlot(customerProfile, slot);
    if (fromSlot) return fromSlot;
  }

  return (
    `${routeCustomer?.customer_address || ""}`.trim() ||
    `${routeCustomer?.customer_route_base_address || ""}`.trim() ||
    ""
  );
};

/** Build route-customer address fields when selecting a profile slot.
 * Clears legacy free-text address fields — slot is the persisted source of truth.
 */
export const buildRouteCustomerAddressFields = (customer, slot) => ({
  customer_address_slot: slot,
  customer_address: "",
  customer_route_base_address: "",
});

/**
 * Normalize route/template customer address fields for persistence:
 * prefer explicit/existing slots, auto-upgrade legacy string matches to slots,
 * and clear free-text address fields when a slot is the source of truth.
 *
 * @param {Array<object>} customerList
 * @param {Array<object>} customerProfiles
 * @param {Array<object>} [existingCustomerList] prior list (e.g. before editor edits)
 */
export const stabilizeRouteCustomerAddressList = (
  customerList = [],
  customerProfiles = [],
  existingCustomerList = []
) => {
  const existingByCustomerId = new Map(
    (existingCustomerList || []).map((c) => [c?.customer_id, c])
  );
  const customerProfileById = new Map(
    (customerProfiles || []).map((c) => [c?.id, c])
  );

  return (customerList || []).map((customerItem) => {
    const customerProfile = customerProfileById.get(customerItem?.customer_id);
    const existingCustomer = existingByCustomerId.get(customerItem?.customer_id);

    if (hasAddressSlot(customerItem)) {
      return Object.assign({}, customerItem, {
        customer_address_slot: customerItem.customer_address_slot,
        customer_address: "",
        customer_route_base_address: "",
      });
    }

    if (hasAddressSlot(existingCustomer) && !hasAddressSlot(customerItem)) {
      return Object.assign({}, customerItem, {
        customer_address_slot: existingCustomer.customer_address_slot,
        customer_address: "",
        customer_route_base_address: "",
      });
    }

    const existingAddress =
      existingCustomer?.customer_address ||
      existingCustomer?.customer_route_base_address ||
      customerItem?.customer_address ||
      customerItem?.customer_route_base_address ||
      "";

    if (!existingAddress || !customerProfile) return customerItem;

    const structuredAddresses = getCustomerAddressSlotOptions(customerProfile).map(
      (option) => option.address
    );
    if (structuredAddresses.length === 0) return customerItem;

    const structuredAddressSet = new Set(
      structuredAddresses.map(normalizeAddressText).filter(Boolean)
    );
    const existingAddressNormalized = normalizeAddressText(existingAddress);
    if (!existingAddressNormalized) return customerItem;

    const matchedSlot = matchAddressTextToSlot(customerProfile, existingAddress);
    if (matchedSlot) {
      const currentNormalized = normalizeAddressText(
        customerItem.customer_address || customerItem.customer_route_base_address || ""
      );
      if (!currentNormalized || currentNormalized === existingAddressNormalized) {
        return Object.assign({}, customerItem, {
          customer_address_slot: matchedSlot,
          customer_address: "",
          customer_route_base_address: "",
        });
      }
    }

    if (!structuredAddressSet.has(existingAddressNormalized)) {
      return Object.assign({}, customerItem, {
        customer_address:
          existingCustomer?.customer_address || existingAddress || customerItem.customer_address,
        customer_route_base_address:
          existingCustomer?.customer_route_base_address ||
          existingCustomer?.customer_address ||
          existingAddress,
        customer_address_slot: null,
      });
    }

    return customerItem;
  });
};
